import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Pagination } from "@mui/material";
import HorizontalProductCard from "../Cards/HorizontalProductCard";
import { useSearchModal } from "../../Context/SearchModalContext";
import { mergeClasses } from "../../utils";

const Recomended = ({ className, ...props }) => {
  const [recomended, setRecomended] = useState([]);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 8,
    totalRecords: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const navigate = useNavigate();
  const { closeModal } = useSearchModal();
  const sectionRef = useRef(null);

  const handlePageChange = (event, value) => {
    setFilter((prev) => ({ ...prev, page: value }));
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const fetchRecomended = () => {
    setLoading(true);
    if (error) setError(false);
    axios
      .get("get-recommended", {
        params: { recommended: true, ...filter },
      })
      .then((response) => {
        const data = response?.data?.data || [];
        setRecomended(data.map((el) => ({ ...el, id: el._id })));
        setFilter((prev) => ({
          ...prev,
          totalRecords: response?.data?.totalRecords,
        }));
      })
      .catch(() => {
        setRecomended([]);
        setError(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRecomended();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.page]);

  const handleCardClick = (product) => {
    if (typeof closeModal === "function") closeModal();
    navigate(`/product-single/${product.slug}`);
  };

  const totalRecords = Number(filter?.totalRecords) || 0;
  const limit = Number(filter?.limit) || 8;
  const pageCount = Math.max(1, Math.ceil(totalRecords / limit));

  return (
    <section className={mergeClasses("gprc-section", className)} ref={sectionRef}>
      <div className="gprc-bg-orb gprc-bg-orb-tr" />
      <div className="gprc-bg-orb gprc-bg-orb-bl" />
      <div className="gprc-bg-grid" aria-hidden="true" />

      <div className="gprc-inner">
        {/* HEADER */}
        <div className="gprc-head">
          <div className="gprc-head-text">
            <div className="gprc-eyebrow">
              <span className="gprc-eyebrow-dot" />
              JUST FOR YOU · CURATED PICKS
            </div>
            <h2 className="gprc-title">
              Recommended <span className="gprc-title-accent">For You</span>
            </h2>
            <p className="gprc-sub">
              Hand-picked gift cards we think you'll love — curated by our team
              based on what's trending right now.
            </p>
          </div>

          <div className="gprc-head-right">
            {!loading && !error && totalRecords > 0 && (
              <div className="gprc-meta-pill">
                <strong>{totalRecords}</strong> recommendations
              </div>
            )}
            <Link to="/shop" className="gprc-viewall">
              Browse all
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
        </div>

        {/* GRID */}
        {loading ? (
          <div className="gprc-grid">
            {Array.from({ length: filter?.limit || 8 }).map((_, i) => (
              <div key={i} className="gprc-skel">
                <div className="gprc-skel-img" />
                <div className="gprc-skel-body">
                  <div className="gprc-skel-line gprc-skel-line-lg" />
                  <div className="gprc-skel-line gprc-skel-line-sm" />
                  <div className="gprc-skel-line gprc-skel-line-md" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="gprc-empty">
            <div className="gprc-empty-icon" aria-hidden="true">⚠</div>
            <h3>Couldn't load recommendations</h3>
            <p>Please check your connection and try again.</p>
            <button
              type="button"
              className="gprc-empty-btn"
              onClick={fetchRecomended}
            >
              Try again
            </button>
          </div>
        ) : recomended.length === 0 ? (
          <div className="gprc-empty">
            <div className="gprc-empty-icon" aria-hidden="true">💎</div>
            <h3>No recommendations yet</h3>
            <p>Curated picks update weekly — check back soon.</p>
            <Link to="/shop" className="gprc-empty-btn">
              Browse all gift cards
            </Link>
          </div>
        ) : (
          <>
            <div className="gprc-grid">
              {recomended.map((product, index) => (
                <div
                  key={product?.id || index}
                  className="gprc-card-wrap"
                  style={{ animationDelay: `${Math.min(index * 50, 480)}ms` }}
                  onClick={() => handleCardClick(product)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCardClick(product);
                    }
                  }}
                >
                  <span className="gprc-pick-badge" aria-hidden="true">
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                    PICK
                  </span>
                  <div className="gprc-card-glow" aria-hidden="true" />
                  <HorizontalProductCard product={product} />
                </div>
              ))}
            </div>

            {pageCount > 1 && (
              <div className="gprc-pagination">
                <Pagination
                  count={pageCount}
                  size="medium"
                  page={filter?.page || 1}
                  onChange={handlePageChange}
                  siblingCount={1}
                  shape="rounded"
                />
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        /* ============ SECTION ============ */
        .gprc-section {
          position: relative;
          background: linear-gradient(180deg, #07060a 0%, #0b0b0c 100%);
          padding: 70px 22px 80px;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          color: #fff;
          border-top: 1px solid rgba(212, 168, 87, 0.08);
        }
        .gprc-bg-orb {
          position: absolute;
          width: 380px;
          height: 380px;
          border-radius: 50%;
          filter: blur(120px);
          pointer-events: none;
          opacity: 0.55;
        }
        .gprc-bg-orb-tr {
          background: rgba(212, 168, 87, 0.18);
          top: -140px;
          right: -100px;
        }
        .gprc-bg-orb-bl {
          background: rgba(212, 168, 87, 0.12);
          bottom: -160px;
          left: -100px;
        }
        .gprc-bg-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(212, 168, 87, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212, 168, 87, 0.04) 1px, transparent 1px);
          background-size: 48px 48px;
          mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
          pointer-events: none;
          opacity: 0.5;
        }

        .gprc-inner {
          position: relative;
          max-width: 1300px;
          margin: 0 auto;
          z-index: 1;
        }

        /* ============ HEADER ============ */
        .gprc-head {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 36px;
          flex-wrap: wrap;
        }
        .gprc-head-text { max-width: 720px; }
        .gprc-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          background: rgba(212, 168, 87, 0.1);
          border: 1px solid rgba(212, 168, 87, 0.3);
          color: #d4a857;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2px;
          margin-bottom: 14px;
        }
        .gprc-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #d4a857;
          box-shadow: 0 0 10px #d4a857;
          animation: gprc-pulse 1.6s ease-in-out infinite;
        }
        @keyframes gprc-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(0.85); }
        }
        .gprc-title {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 38px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 8px;
          line-height: 1.15;
          letter-spacing: -0.4px;
        }
        .gprc-title-accent {
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          font-style: italic;
          font-weight: 600;
        }
        .gprc-sub {
          font-size: 14.5px;
          color: #9aa0a8;
          line-height: 1.55;
          margin: 0;
          max-width: 580px;
        }

        .gprc-head-right {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .gprc-meta-pill {
          padding: 8px 14px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.2);
          color: #cfd2d6;
          border-radius: 999px;
          font-size: 12px;
        }
        .gprc-meta-pill strong { color: #d4a857; font-weight: 700; margin-right: 4px; }

        .gprc-viewall {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 11px 18px;
          background: linear-gradient(135deg, #f1cf85 0%, #d4a857 50%, #b58a3f 100%);
          color: #1a1208 !important;
          border: 1px solid rgba(255, 232, 178, 0.5);
          border-radius: 999px;
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 0.6px;
          text-decoration: none;
          box-shadow: 0 6px 16px rgba(212, 168, 87, 0.3);
          transition: transform .25s ease, box-shadow .25s ease, filter .25s ease;
        }
        .gprc-viewall:hover {
          color: #1a1208 !important;
          text-decoration: none;
          transform: translateY(-1px);
          filter: brightness(1.05);
          box-shadow: 0 10px 22px rgba(212, 168, 87, 0.45);
        }
        .gprc-viewall svg { transition: transform .2s ease; }
        .gprc-viewall:hover svg { transform: translateX(2px); }

        /* ============ GRID ============ */
        .gprc-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 18px;
        }
        .gprc-card-wrap {
          position: relative;
          opacity: 0;
          transform: translateY(14px);
          animation: gprc-fadeup .55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
          transition: transform .35s cubic-bezier(0.22, 1, 0.36, 1);
          border-radius: 14px;
          cursor: pointer;
          outline: none;
        }
        .gprc-card-wrap:hover { transform: translateY(-5px); }
        .gprc-card-wrap:focus-visible {
          box-shadow: 0 0 0 3px rgba(212, 168, 87, 0.45);
        }
        @keyframes gprc-fadeup {
          to { opacity: 1; transform: translateY(0); }
        }
        .gprc-card-glow {
          position: absolute;
          inset: -2px;
          border-radius: 16px;
          background: linear-gradient(135deg, rgba(212, 168, 87, 0.55), rgba(180, 130, 40, 0));
          opacity: 0;
          transition: opacity .3s ease;
          pointer-events: none;
          z-index: 0;
          filter: blur(8px);
        }
        .gprc-card-wrap:hover .gprc-card-glow,
        .gprc-card-wrap:focus-visible .gprc-card-glow { opacity: 0.55; }

        /* ----- PICK BADGE (heart icon) ----- */
        .gprc-pick-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          z-index: 3;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 9.5px;
          font-weight: 800;
          letter-spacing: 0.7px;
          padding: 4px 9px;
          border-radius: 999px;
          color: #fff;
          background: linear-gradient(135deg, #ff6b6b, #c0392b);
          border: 1px solid rgba(255, 200, 200, 0.35);
          box-shadow: 0 5px 12px rgba(192, 57, 43, 0.4);
        }
        .gprc-pick-badge svg {
          color: #fff;
          filter: drop-shadow(0 0 3px rgba(255, 200, 200, 0.4));
        }

        /* ----- DARK CARD STYLING (overrides on HorizontalProductCard) ----- */
        .gprc-card-wrap .horizontal-product-item {
          position: relative;
          z-index: 1;
          display: flex !important;
          align-items: stretch;
          gap: 12px !important;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%) !important;
          border: 1px solid rgba(212, 168, 87, 0.18) !important;
          border-radius: 14px !important;
          padding: 12px !important;
          box-shadow: 0 8px 22px rgba(0, 0, 0, 0.35) !important;
          color: #fff !important;
          text-decoration: none !important;
          transition: border-color .3s ease, box-shadow .3s ease !important;
          height: 100%;
          min-height: 130px;
          pointer-events: none; /* clicks bubble to .gprc-card-wrap onClick */
        }
        .gprc-card-wrap:hover .horizontal-product-item,
        .gprc-card-wrap:focus-visible .horizontal-product-item {
          border-color: rgba(212, 168, 87, 0.55) !important;
          box-shadow: 0 14px 32px rgba(212, 168, 87, 0.18), 0 6px 14px rgba(0, 0, 0, 0.4) !important;
        }
        .gprc-card-wrap .horizontal-product-item .product-image {
          flex-shrink: 0 !important;
          width: 88px !important;
          height: 88px !important;
          background: #0b0b0c !important;
          border: 1px solid rgba(212, 168, 87, 0.15) !important;
          border-radius: 10px !important;
          overflow: hidden !important;
          padding: 0 !important;
          margin: 0 !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .gprc-card-wrap .horizontal-product-item .product-image img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          transition: transform .5s cubic-bezier(0.22, 1, 0.36, 1) !important;
        }
        .gprc-card-wrap:hover .horizontal-product-item .product-image img {
          transform: scale(1.05) !important;
        }
        .gprc-card-wrap .horizontal-product-item .product-info {
          flex: 1 !important;
          min-width: 0 !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 4px !important;
          padding: 2px 0 !important;
          margin: 0 !important;
        }
        .gprc-card-wrap .horizontal-product-item .product-card-title {
          color: #fff !important;
          font-size: 13.5px !important;
          font-weight: 600 !important;
          line-height: 1.35 !important;
          margin: 0 !important;
          padding-right: 50px !important; /* leave room for the PICK badge */
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .gprc-card-wrap:hover .horizontal-product-item .product-card-title {
          color: #d4a857 !important;
        }
        .gprc-card-wrap .horizontal-product-item .product-info > div {
          margin: 0 !important;
        }
        .gprc-card-wrap .horizontal-product-item .product-info .flex {
          gap: 6px !important;
        }
        .gprc-card-wrap .horizontal-product-item .product-info span {
          color: #9aa0a8;
          font-size: 11px;
        }
        .gprc-card-wrap .horizontal-product-item .price {
          margin-top: auto !important;
          padding-top: 6px !important;
          border-top: 1px solid rgba(212, 168, 87, 0.12) !important;
        }
        .gprc-card-wrap .horizontal-product-item .present-price {
          color: #d4a857 !important;
          font-weight: 700 !important;
          font-size: 14px !important;
        }

        /* ============ SKELETON ============ */
        .gprc-skel {
          display: flex;
          gap: 12px;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.12);
          border-radius: 14px;
          padding: 12px;
          min-height: 130px;
        }
        .gprc-skel-img {
          flex-shrink: 0;
          width: 88px;
          height: 88px;
          border-radius: 10px;
          background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
          background-size: 200% 100%;
          animation: gprc-shimmer 1.4s linear infinite;
        }
        .gprc-skel-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
          justify-content: center;
        }
        .gprc-skel-line {
          height: 11px;
          border-radius: 4px;
          background: linear-gradient(90deg, #1a1208 0%, #2a1f10 50%, #1a1208 100%);
          background-size: 200% 100%;
          animation: gprc-shimmer 1.4s linear infinite;
        }
        .gprc-skel-line-lg { height: 14px; width: 85%; }
        .gprc-skel-line-md { width: 50%; }
        .gprc-skel-line-sm { width: 35%; }
        @keyframes gprc-shimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }

        /* ============ EMPTY / ERROR ============ */
        .gprc-empty {
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px dashed rgba(212, 168, 87, 0.3);
          border-radius: 16px;
          padding: 56px 24px;
          text-align: center;
          max-width: 540px;
          margin: 0 auto;
        }
        .gprc-empty-icon {
          font-size: 36px;
          color: #d4a857;
          margin-bottom: 12px;
        }
        .gprc-empty h3 {
          font-size: 18px;
          color: #fff;
          margin: 0 0 6px;
          font-weight: 700;
        }
        .gprc-empty p {
          color: #9aa0a8;
          margin: 0 0 20px;
          font-size: 13.5px;
        }
        .gprc-empty-btn {
          display: inline-flex;
          align-items: center;
          background: linear-gradient(135deg, #d4a857, #b58a3f);
          color: #1a1208 !important;
          border: none;
          padding: 11px 22px;
          border-radius: 10px;
          font-weight: 700;
          font-size: 13px;
          letter-spacing: 0.5px;
          cursor: pointer;
          text-decoration: none;
          transition: transform .2s ease, filter .2s ease;
          font-family: inherit;
        }
        .gprc-empty-btn:hover {
          transform: translateY(-2px);
          filter: brightness(1.05);
          color: #1a1208 !important;
          text-decoration: none;
        }

        /* ============ PAGINATION ============ */
        .gprc-pagination {
          display: flex;
          justify-content: center;
          padding: 40px 0 0;
        }
        .gprc-pagination .MuiPaginationItem-root {
          color: #cfd2d6 !important;
          font-weight: 600;
          border-radius: 10px;
          margin: 0 2px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(212, 168, 87, 0.15);
        }
        .gprc-pagination .MuiPaginationItem-root:hover {
          background: rgba(212, 168, 87, 0.15) !important;
          color: #d4a857 !important;
          border-color: rgba(212, 168, 87, 0.4);
        }
        .gprc-pagination .MuiPaginationItem-root.Mui-selected {
          background: linear-gradient(135deg, #d4a857, #b58a3f) !important;
          color: #1a1208 !important;
          border-color: transparent !important;
          box-shadow: 0 6px 14px rgba(212, 168, 87, 0.3);
        }
        .gprc-pagination .MuiPaginationItem-root.Mui-selected:hover {
          background: linear-gradient(135deg, #f1cf85, #d4a857) !important;
        }

        /* ============ RESPONSIVE ============ */
        @media (max-width: 1100px) {
          .gprc-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 820px) {
          .gprc-section { padding: 56px 18px 64px; }
          .gprc-title { font-size: 28px; }
          .gprc-sub { font-size: 13.5px; }
          .gprc-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
        }
        @media (max-width: 480px) {
          .gprc-section { padding: 44px 14px 52px; }
          .gprc-title { font-size: 22px; }
          .gprc-eyebrow { font-size: 10px; letter-spacing: 1.5px; }
          .gprc-grid { grid-template-columns: 1fr; }
          .gprc-head { gap: 14px; flex-direction: column; align-items: flex-start; }
          .gprc-head-right { width: 100%; }
          .gprc-viewall { flex: 1; justify-content: center; }
          .gprc-card-wrap .horizontal-product-item .product-image,
          .gprc-skel-img { width: 80px !important; height: 80px !important; }
        }
      `}</style>
    </section>
  );
};

export default Recomended;
